import { Button } from '@material-ui/core';
import ArrowBack from '@material-ui/icons/ArrowBack';
import ArrowRightIcon from '@mui/icons-material/ArrowRight';
import CancelIcon from '@mui/icons-material/Cancel';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  DASHBOARD_REST_RESERV_VIEW,
  DASHBOARD_REST_TABLES_VIEW,
  REST_STATUS_ARRIVING_TODAY,
  REST_STATUS_COMPLETED,
  REST_STATUS_DECLINED,
  REST_STATUS_GUEST_CANCELLED,
  REST_STATUS_NEW_REQUEST,
  REST_STATUS_NO_SHOWS,
  REST_STATUS_SCHEDULED,
} from '../../../../constants/DashboardConst';
import {
  fetchRestaurantDashboardReservationDetails,
  restaurantDashboardReservationsSelector,
  setDashboardNewOrder,
} from '../../DashboardSlice';
import { selectedHotelSelector } from '../../../property/propertySlice';
import { formatDate } from '../../../../utils/dashboardUtils';
import './DashboardRestaurantView.scss';
import LinearProgressBar from '../../../../shared/progress-bar/Progress';
import moment from 'moment';
import AssignTable from './AssignTable';
import { notificationManager } from '../../../../utils/NotificationUtils';
import {
  assignRestaurantTable,
  updateRestaurantDashboardOrderStatus,
} from '../../../../utils/dashboardAPIUtils';
import ActivationPopupModel from '../../../../shared/activation-popup/Activation-Popup';
import { decryptLocalStorage } from '../../../../utils/authUtils';
import { stepDownLoaderCount, stepUpLoaderCount } from '../../../../utils/loaderSlice';

const DashboardRestaurantView = (props) => {
  const { obj, onViewToggle, showLoader = () => {} } = props;
  const dispatch = useDispatch();
  const allDashboardRestaurantList = useSelector(restaurantDashboardReservationsSelector);
  const selectedHotel = useSelector(selectedHotelSelector);
  const [order, setOrder] = useState(null);
  const [selectedValue, setSelectedValue] = useState({
    id: '',
    name: '',
    title: '',
    description: '',
    status: false,
    type: '',
    descriptionClass: 'delete-popup-description',
  });

  useEffect(() => {
    const currOrder = allDashboardRestaurantList.find((item) => item.id === obj.id);
    if (currOrder) setOrder(currOrder);
  }, [obj, allDashboardRestaurantList]);

  const onClickCompleteRequest = () => {
    if (order?.status === REST_STATUS_ARRIVING_TODAY || order?.status === REST_STATUS_SCHEDULED) {
      setSelectedValue({
        id: order.id,
        name: '',
        title: 'Complete Appointment ?',
        description: 'This action cannot be reversed.',
        status: true,
        type: 'complete',
        descriptionClass: 'delete-popup-description-black',
      });
    }
  };

  const onClickGuestCancelled = () => {
    const btnName = getBtnName('btn3');
    const currSelectedValue = {
      id: order.id,
      name: '',
      title: '',
      description:
        'This scheduled reservation will be removed from the list. This action cannot be reversed.',
      status: true,
      type: '',
      descriptionClass: 'delete-popup-description-black',
    };
    if (btnName === 'Guest Cancelled') {
      currSelectedValue.title = 'Oh no! Guest Cancelled ?';
      currSelectedValue.type = 'guestCancel';
    } else {
      currSelectedValue.title = 'Guest Did not show up?';
      currSelectedValue.type = 'noShow';
    }
    setSelectedValue(currSelectedValue);
  };

  const onUpdatingStatus = async (newStatus, successMessage, errorMessage) => {
    try {
      dispatch(stepUpLoaderCount());
      const lang = decryptLocalStorage('lang');
      const language = lang !== 'en' ? lang : '';

      if (newStatus === 'assignTable') {
        return setSelectedValue({
          id: order?.id,
          name: `${order?.firstName} ${order?.lastName}`,
          title: 'Assign Table to',
          description: '',
          status: true,
          type: 'assignTable',
          descriptionClass: '',
          order: order,
        });
      }
      if (order?.id) {
        await updateRestaurantDashboardOrderStatus(
          order.restaurantId,
          order.id,
          newStatus,
          language,
        );
        dispatch(fetchRestaurantDashboardReservationDetails(order.restaurantId, selectedHotel));
        notificationManager('success', successMessage);
      }
    } catch (error) {
      notificationManager(
        'danger',
        error?.graphQLErrors ? error?.graphQLErrors?.[0]?.message : errorMessage,
      );
    } finally {
      dispatch(stepDownLoaderCount());
    }
    return true;
  };

  const onClickDeclineRequest = () => {
    if (order?.status !== REST_STATUS_NEW_REQUEST) return false;
    setSelectedValue({
      id: order.id,
      name: '',
      title: 'Reject Request?',
      description:
        'Are you sure you want to reject this request? An email will be sent to ' +
        order?.firstName +
        ' ' +
        order?.lastName +
        ' regarding their booking request status',
      status: true,
      type: 'reject',
      descriptionClass: 'delete-popup-description-black',
    });
  };

  const onClickBack = () => {
    dispatch(setDashboardNewOrder(null));
    onViewToggle('', false, null);
  };

  const onOkClick = () => {
    try {
      dispatch(stepUpLoaderCount());
      if (selectedValue?.type === 'reject') {
        onUpdatingStatus(
          REST_STATUS_DECLINED,
          'Request Rejected successfully',
          'Error occurred while rejecting request',
        );
      }
      if (selectedValue?.type === 'guestCancel') {
        onUpdatingStatus(
          REST_STATUS_GUEST_CANCELLED,
          'Request Cancelled successfully',
          'Error occurred while cancelling request',
        );
      }
      if (selectedValue?.type === 'complete') {
        onUpdatingStatus(
          REST_STATUS_COMPLETED,
          'Request Completed successfully',
          'Error occurred while completing request',
        );
      }
      if (selectedValue?.type === 'noShow') {
        onUpdatingStatus(
          REST_STATUS_NO_SHOWS,
          'Request Completed successfully',
          'Error occurred while completing request',
        );
      }
      handleClose();
    } catch (error) {
      notificationManager(
        'danger',
        `${
          error?.graphQLErrors ? error?.graphQLErrors?.[0]?.message : 'Error occurred while viewing'
        }`,
      );
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const handleClose = () => {
    setSelectedValue({
      id: '',
      name: '',
      title: '',
      description: '',
      status: false,
      type: '',
      descriptionClass: 'delete-popup-description',
    });
  };

  const onAssignTable = async (reservedTables) => {
    try {
      dispatch(stepUpLoaderCount());
      const lang = decryptLocalStorage('lang');
      const language = lang !== 'en' ? lang : '';
      const payload = {
        id: obj.id,
        restaurantId: obj.restaurantId,
        tableNumbers: reservedTables,
      };
      await assignRestaurantTable(payload, language);
      dispatch(fetchRestaurantDashboardReservationDetails(order.restaurantId, selectedHotel));
      notificationManager('success', 'Table assigned successfully');
      handleClose();
    } catch (e) {
      notificationManager(
        'danger',
        e.graphQLErrors ? e.graphQLErrors[0].message : 'Error occurred while reversing table',
      );
    } finally {
      dispatch(stepDownLoaderCount());
    }
  };

  const getBtnName = (btnType) => {
    let btnName = '';
    switch (btnType) {
      case 'btn1':
        if (order?.status === REST_STATUS_NEW_REQUEST) btnName = 'Accept Request';
        else if (
          (order?.status === REST_STATUS_SCHEDULED ||
            order?.status === REST_STATUS_ARRIVING_TODAY) &&
          (!order?.tableNumbers || order?.tableNumbers.length === 0)
        )
          btnName = 'Assign Table';
        else btnName = 'Change Table Number';
        break;
      case 'btn3':
        if (
          (order?.status === REST_STATUS_SCHEDULED ||
            order?.status === REST_STATUS_ARRIVING_TODAY) &&
          (!order?.tableNumbers || order?.tableNumbers.length === 0)
        )
          btnName = 'Guest Cancelled';
        else btnName = 'No Show';
        break;
      default:
        break;
    }
    return btnName;
  };

  const getBtnVariant = () => {
    const btnName = getBtnName('btn1');
    if (btnName === 'Accept Request' || btnName === 'Assign Table') return 'contained';
    return 'outlined';
  };

  return (
    <>
      {order === null ? (
        <div className='pt-4 col-md-12'>
          <LinearProgressBar />
        </div>
      ) : (
        <div className='rest-dashboard-view container mt-4 mb-3 detail-view-65 pb-4'>
          <div className='mt-5 row mx-0'>
            <div className='col-md-6 px-0 d-flex align-items-center'>
              <Button
                variant='text'
                color='secondary'
                size='small'
                startIcon={<ArrowBack />}
                onClick={(e) => onClickBack()}
                className='me-2'
              >
                Back
              </Button>
              <span className='page-title'>
                {order?.firstName} {order?.lastName}
              </span>
            </div>
            <div className='col-md-6 d-flex justify-content-end'>
              <Button
                className='py-2 me-4'
                variant='outlined'
                color='secondary'
                size='medium'
                endIcon={<ArrowRightIcon />}
                onClick={() => onViewToggle(DASHBOARD_REST_TABLES_VIEW, true, obj)}
              >
                Tables
              </Button>
              <Button
                className='py-2'
                variant='outlined'
                color='secondary'
                size='medium'
                endIcon={<ArrowRightIcon />}
                onClick={() => onViewToggle(DASHBOARD_REST_RESERV_VIEW, true, obj)}
              >
                Reservations
              </Button>
            </div>
          </div>
          <div className='card shadow mt-4 mb-4 pb-5 h-100 px-4 pt-4'>
            <div className='row mb-4'>
              <div className='col-md-5'>
                <div className='details-label'>Request Id</div>
                <div className='details-value'>{order?.id}</div>
              </div>
              <div className='col-md-4'>
                <div className='details-label'>Booking Date & Time</div>
                <div className='details-value'>
                  {formatDate(order?.bookingDate, 'DD MMM YYYY, hh:mm a')}
                </div>
              </div>
              <div className='col-md-3'>
                <div className='details-label'>Status</div>
                <div className={`status ${order?.statusClass}`}>{order?.orderStatus}</div>
              </div>
            </div>
            <div className='row mb-4'>
              <div className='col-md-5'>
                <div className='details-label'>Guest name</div>
                <div className='details-value'>
                  {order?.firstName} {order?.lastName}
                </div>
              </div>
              <div className='col-md-4'>
                <div className='details-label'>Guest Type</div>
                <div className='details-value'>
                  {order.guestType === 'resident'
                    ? `Resident - Room ${order?.roomNo === '' ? '---' : order?.roomNo}`
                    : 'Non Resident'}
                </div>
              </div>
            </div>
            <div className='row mb-4'>
              <div className='col-md-5'>
                <div className='details-label'>
                  {order?.status === REST_STATUS_ARRIVING_TODAY ||
                  order?.status === REST_STATUS_SCHEDULED ||
                  order?.status === REST_STATUS_COMPLETED ||
                  order?.status === REST_STATUS_GUEST_CANCELLED
                    ? 'Scheduled Date'
                    : 'Requested Date'}
                </div>
                <div className='details-value'>{formatDate(order?.date, 'DD MMM YYYY')}</div>
              </div>
              <div className='col-md-4'>
                <div className='details-label'>
                  {order?.status === REST_STATUS_ARRIVING_TODAY ||
                  order?.status === REST_STATUS_SCHEDULED ||
                  order?.status === REST_STATUS_COMPLETED ||
                  order?.status === REST_STATUS_GUEST_CANCELLED
                    ? 'Scheduled Time Slot'
                    : 'Requested Time Slot'}
                </div>
                <div className='details-value'>
                  {moment(order?.reserveFrom, 'HH:mm').format('hh:mm A')} -{' '}
                  {moment(order?.reserveUntil, 'HH:mm').format('hh:mm A')}
                </div>
              </div>
            </div>
            <div className='row mb-4'>
              <div className='col-md-5'>
                <div className='details-label'>No of Guests</div>
                <div className='details-value'>{('0' + order?.noOfGuests).slice(-2)}</div>
              </div>
              {order?.tableNumbers.length > 0 && (
                <div className='col-md-4'>
                  <div className='details-label'>Table number</div>
                  <div className='details-value'>
                    {order?.tableNumbers?.map((tableNumber) => tableNumber).join(',')}
                  </div>
                </div>
              )}
            </div>
            <div className='row mb-4'>
              <div className='col-md-5'>
                <div className='details-label'>Special Requests</div>
                <div className='details-value'>{order?.description}</div>
              </div>
            </div>

            <div className='row mt-5'>
              <div className='col-md-6'>
                {(order?.status === REST_STATUS_NEW_REQUEST ||
                  order?.status === REST_STATUS_ARRIVING_TODAY ||
                  order?.status === REST_STATUS_SCHEDULED) && (
                  <Button
                    variant={getBtnVariant()}
                    color='primary'
                    type='button'
                    onClick={() =>
                      onUpdatingStatus(
                        order?.status === REST_STATUS_NEW_REQUEST
                          ? REST_STATUS_SCHEDULED
                          : 'assignTable',
                        'Request accepted successfully',
                        'Error occurred while accepting request',
                      )
                    }
                  >
                    {getBtnName('btn1')}
                  </Button>
                )}
                {order?.status === REST_STATUS_NEW_REQUEST && (
                  <Button
                    variant='outlined'
                    type='button'
                    className='rest-reject-request-button mx-4'
                    onClick={() => onClickDeclineRequest()}
                  >
                    <span className='mx-2'>
                      {order?.status === REST_STATUS_DECLINED
                        ? 'Request Declined'
                        : 'Decline Request'}
                    </span>
                    {order?.status !== REST_STATUS_DECLINED && <CancelIcon />}
                  </Button>
                )}
              </div>
              <div className='col-md-6 d-flex justify-content-end'>
                {(order?.status === REST_STATUS_ARRIVING_TODAY ||
                  order?.status === REST_STATUS_SCHEDULED) && (
                  <Button
                    variant='outlined'
                    color='secondary'
                    type='button'
                    className='btn-delayed mx-4'
                    onClick={() => onClickGuestCancelled()}
                  >
                    {getBtnName('btn3')}
                  </Button>
                )}
                {(order?.status === REST_STATUS_ARRIVING_TODAY ||
                  order?.status === REST_STATUS_SCHEDULED) && (
                  <Button
                    variant='contained'
                    color='primary'
                    type='button'
                    onClick={() => onClickCompleteRequest()}
                  >
                    Complete Reservation
                  </Button>
                )}
                {order?.status === REST_STATUS_COMPLETED && (
                  <Button
                    className='completed-button-static'
                    variant='outlined'
                    color='primary'
                    type='button'
                  >
                    Reservation Completed
                  </Button>
                )}
              </div>
            </div>
          </div>
          {selectedValue.status &&
            (selectedValue.type === 'reject' ||
              selectedValue.type === 'guestCancel' ||
              selectedValue.type === 'noShow' ||
              selectedValue.type === 'complete') && (
              <ActivationPopupModel
                activationPopUp={selectedValue}
                cancelButtonName='No'
                okButtonName='Yes'
                onOkClick={onOkClick}
                onActivationPopupActionCancel={handleClose}
              />
            )}
          {selectedValue.status && selectedValue.type === 'assignTable' && (
            <AssignTable
              assignObj={selectedValue}
              onAssignTable={onAssignTable}
              handleClose={handleClose}
              order={order}
            />
          )}
        </div>
      )}
    </>
  );
};

export default DashboardRestaurantView;
